import React from 'react';
import { classNames } from '@progress/kendo-react-common';
import { Input as KendoInput } from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import { PureComponent } from '../../../../dispatcher/modules';
import {
  PUZZLE_PASSWORD_INPUT_INPUT_PROPS,
  PUZZLE_PASSWORD_INPUT_PROPS
} from './Types';
import { PUZZLE_FIELD_STATE } from '../Types';
import Field from '../../../layout/field/Field';
import WithTooltip from '../../../../utility/withTooltip';
import PuzzleTooltip from '../../tooltip/PuzzleTooltip';
import {
  ABSTRACT_FIELD_OMITTED_PROPS,
  FIELD_OMITTED_PROPS, 
  PASSWORD_FIELD_OMITTED_PROPS,
  TOOLTIP_OMITTED_PROPS
} from '../../../../utility';
import { Label } from '@progress/kendo-react-labels';
import PuzzleCopyButton from '../../button/copy/PuzzleCopyButton';
import uuid from 'uuid-random';
import PuzzleButton from '../../button/PuzzleButton';
import HBox from '../../../layout/container/h_box/HBox';
import { BUTTON_SIZE_PROP } from '../../button/Types';

/**
 * This component extends Kendo's Input to support password input functionality,
 * including password visibility toggling and customizable copy button.
 * It provides state management for various states (empty/hover/focus/disabled/invalid).
 * The component can be presented with a password visibility toggle icon on the inside
 * and a copy button on the inside or outside.
 **/

class PuzzlePasswordInput extends PureComponent<PUZZLE_PASSWORD_INPUT_PROPS, PUZZLE_FIELD_STATE> {
  moduleId = uuid();
  tooltip: any = null;
  inputID: any = null;

  static defaultProps = {
    tooltipAnchor: 'target',
    tooltipOpenDelay: 1000,
    fieldSize: 'medium'
  };

  /**
   * Sets field valid state
   * @param resourceExists
   */
  setValid = (resourceExists: boolean) => {
    _.get(this, 'props.setValid') && this.props.setValid(resourceExists);
  };

  renderBody() {
    const {
      asField,
      ariaDescribedBy,
      className = '',
      dataCy = 'puzzle-input-wrap',
      disabled,
      inputClassName = '',
      label,
      labelClassName = '',
      locked = false,
      errorTxt,
      value,
      valid,
      fieldSize,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      withTooltip,
      tooltipAnchor,
      tooltipContent,
      tooltipOpenDelay,
      tooltipPosition,
      tooltipClassName = '',
      titleText,
      customDataCy,
      withShowPasswordIcon,
      togglePasswordVisibility,
      inputType = 'password',
      showCopy,
      copyButtonProps,
      copyButtonPosition
    } = this.props;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const inputOmittedProps = [
      'dataCy', 'inputClassName', 'onFocus', 'customDataCy', 'inputType', 'fieldSize', 'locked'
    ].concat(PASSWORD_FIELD_OMITTED_PROPS, ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS, TOOLTIP_OMITTED_PROPS);

    const inputProps:PUZZLE_PASSWORD_INPUT_INPUT_PROPS = _.omit(this.props, inputOmittedProps);

    const fieldCls = classNames(
      `tp-textbox-wrap tp-password-input tp-input-with-multiple-actions ${className} k-flex-1 k-d-flex-col`,
      {
        'k-state-empty': !value,
        'k-small': fieldSize === 'small',
        'k-large': fieldSize === 'large',
        'k-medium': fieldSize === 'medium',
        'k-state-invalid': valid === false,
        'k-state-disabled': disabled,
        'k-state-locked': locked
      }
    );

    const toggleVisibilityIconClass = inputType === 'password' ? 'tp-visibility_on' : 'tp-visibility_off';

    const inputContent = (
      <KendoInput
        {...inputProps}
        id={this.inputID}
        className={inputClassName}
        data-cy={dataCy && customDataCy ? dataCy : customDataCy ? undefined : 'puzzle-input'}
        title={titleText}
        tabIndex={locked ? -1 : 0}
        type={inputType}
      />
    );

    let btnSize: BUTTON_SIZE_PROP = 'small';
    if (fieldSize === 'large') {
      btnSize = 'medium';
    }

    const content = (
      <div
        className={fieldCls}
        data-cy={dataCy && customDataCy ? `${dataCy}-wrapper` : customDataCy ? undefined : dataCy}
      >
        {label && <Label
          editorId={this.inputID}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {label}
        </Label>}
        <HBox
          gap={1}
          alignItemsCenter
          className={`tp-textbox ${this.state.isInputFocused ? 'k-focus' : ''}`}
        >
          {withTooltip ?
            <WithTooltip
              className={tooltipClassName}
              context={this}
              title={titleText}
              htmlTag={'span'}
            >
              {inputContent}
            </WithTooltip> : inputContent
          }
          <HBox gap={1} className={'tp-actions-after'} alignItemsCenter>
            {showCopy && copyButtonPosition === 'inside' && <PuzzleCopyButton
              {...copyButtonProps}
              size={btnSize}
              extraSmall={fieldSize === 'small'}
              copyButtonPosition={copyButtonPosition}
              inputValue={value}
            />}
            {withShowPasswordIcon &&
            <PuzzleButton
              aria-hidden={true}
              className={'k-hbox k-align-items-center tp-show-password-btn'}
              iconClass={`tp-icon ${toggleVisibilityIconClass}`}
              size={btnSize}
              extraSmall={fieldSize === 'small'}
              fillMode={'flat'}
              data-cy="show-password-icon"
              onClick={togglePasswordVisibility}
            />
            }
          </HBox>
        </HBox>
        {withValidation && <div
          data-cy={dataCy && customDataCy ? `${dataCy}-error-message` : customDataCy ? undefined : 'error-message'}
          className={'tp-validation-error-message'}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
        <PuzzleTooltip
          anchorElement={tooltipAnchor}
          ref={(el: any) => this.tooltip = el}
          openDelay={tooltipOpenDelay}
          position={tooltipPosition}
          content={tooltipContent}
        />
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }
    return content;
  }
}

export default PuzzlePasswordInput;
